import type { ConsentSnapshot } from "./types";
export const TEST_ENROLLMENT_SOURCE = "prospix_test_enrollment_v1";

export function isActiveTestEnrollment(
  snapshot: ConsentSnapshot,
  at = Date.now(),
) {
  return (
    snapshot.state === "granted" &&
    !snapshot.gpc &&
    !snapshot.dnt &&
    snapshot.cmp?.source === TEST_ENROLLMENT_SOURCE &&
    /^test-session-[a-f0-9]{64}$/.test(
      snapshot.cmp.testEnrollmentToken ?? "",
    ) &&
    typeof snapshot.cmp.testEnrollmentExpiresAt === "number" &&
    snapshot.cmp.testEnrollmentExpiresAt > at
  );
}

/** A saved host-banner preference predates the separately joined test. */
export function isRestoredHostChoiceDuringTest(
  snapshot: ConsentSnapshot,
  payload: {
    cmp?: { source?: string };
    transitionVersion?: number;
    occurredAt?: number;
  },
) {
  return (
    isActiveTestEnrollment(snapshot) &&
    payload.cmp?.source === "marketing_banner_v1" &&
    payload.transitionVersion === undefined &&
    payload.occurredAt === undefined
  );
}

/** Remove the invitation before page/context analytics can observe its URL. */
export function takeTestInvitation(win: Window): string | null {
  const params = new URLSearchParams((win.location?.hash ?? "").slice(1));
  const token = params.get("prospix_test_invite");
  if (!token || !/^test-invite-[A-Za-z0-9_-]{43}$/.test(token)) return null;
  params.delete("prospix_test_invite");
  const url = new URL(win.location.href);
  url.hash = params.toString();
  win.history.replaceState(win.history.state, "", url.toString());
  return token;
}

export type TestCaptureStatus = "checking" | "active" | "blocked";

export function testCapturePresentation(
  state: TestCaptureStatus,
  expiresAt: number,
) {
  const until = new Date(expiresAt).toLocaleString();
  if (state === "blocked")
    return {
      title: "Test capture unavailable",
      text: "The server did not accept test activity. Check the invitation or contact the website owner. Use Stop test to withdraw.",
    };
  if (state === "checking")
    return {
      title: "Checking website test",
      text: `Test permission was saved until ${until}. Waiting for the server to confirm capture. Use Stop test to withdraw.`,
    };
  return {
    title: "Website test active",
    text: `Activity and submitted contacts can be captured until ${until}. Banner buttons record test answers. Use Stop test to withdraw.`,
  };
}

export function updateTestCaptureStatus(
  state: TestCaptureStatus,
  expiresAt: number,
) {
  const root = document.getElementById("prospix-test-enrollment")?.shadowRoot;
  const title = root?.querySelector("h2"),
    text = root?.querySelector("p");
  const copy = testCapturePresentation(state, expiresAt);
  if (title) title.textContent = copy.title;
  if (text) text.textContent = copy.text;
}

export function mountTestEnrollmentPrompt(options: {
  invitation: string | null;
  expiresAt?: number;
  captureStatus?: TestCaptureStatus;
  join: () => Promise<number>;
  stop: () => Promise<void>;
}) {
  if (!options.invitation && !options.expiresAt) return;
  if (document.getElementById("prospix-test-enrollment")) return;
  const host = document.createElement("div");
  host.id = "prospix-test-enrollment";
  host.style.cssText =
    "position:fixed;bottom:20px;right:20px;max-width:min(360px,calc(100vw - 40px));z-index:2147483646";
  const root = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent =
    ":host{font:15px/1.5 system-ui;color:#172132}section{background:#fff;border:1px solid #ccd4dd;border-radius:14px;padding:20px;box-shadow:0 8px 32px #17213226}h2{font-size:20px;margin:0 0 8px}p{margin:8px 0}label{display:flex;gap:10px;align-items:start;margin:14px 0}input{margin-top:5px}button{border:0;background:#146f68;color:white;border-radius:8px;padding:10px 16px;font:inherit;font-weight:600;cursor:pointer}button:disabled{opacity:.5;cursor:default}button:focus-visible{outline:3px solid #74bcb5;outline-offset:3px}[role=status]{font-size:14px}@media(max-width:760px){:host{position:static!important;display:block;max-width:none!important;margin:12px}section{padding:16px}h2{font-size:18px}}";
  const panel = document.createElement("section"),
    title = document.createElement("h2"),
    text = document.createElement("p"),
    status = document.createElement("p"),
    button = document.createElement("button");
  button.type = "button";
  status.setAttribute("role", "status");
  const active = (expiresAt: number, state: TestCaptureStatus = "active") => {
    const copy = testCapturePresentation(state, expiresAt);
    title.textContent = copy.title;
    text.textContent = copy.text;
    button.textContent = "Stop test";
    button.disabled = false;
    button.onclick = async () => {
      button.disabled = true;
      try {
        await options.stop();
        host.remove();
      } catch {
        status.textContent =
          "Collection has stopped locally. Connection is needed to confirm withdrawal.";
        button.textContent = "Confirm withdrawal";
        button.disabled = false;
      }
    };
  };
  panel.append(title, text);
  if (options.expiresAt)
    active(options.expiresAt, options.captureStatus ?? "checking");
  else {
    title.textContent = "Join a website test";
    text.textContent =
      "This invitation permits activity and submitted-contact capture in this browser. Advertising destinations and automated follow-up stay off. You can stop the test at any time.";
    const label = document.createElement("label"),
      checkbox = document.createElement("input"),
      words = document.createElement("span");
    checkbox.type = "checkbox";
    words.textContent = "I agree to participate and permit this test capture.";
    label.append(checkbox, words);
    panel.append(label);
    button.textContent = "Join test";
    button.disabled = true;
    checkbox.onchange = () => {
      button.disabled = !checkbox.checked;
    };
    button.onclick = async () => {
      button.disabled = true;
      status.textContent = "Starting test…";
      try {
        const expiresAt = await options.join();
        label.remove();
        status.textContent = "";
        active(expiresAt);
      } catch {
        status.textContent =
          "The test could not start. The invitation may have expired or a privacy restriction is active.";
        button.disabled = !checkbox.checked;
      }
    };
  }
  panel.append(button, status);
  root.append(style, panel);
  (document.body ?? document.documentElement).prepend(host);
}
